
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, User, Filter } from "lucide-react";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("newest");
  const [categoryFilter, setCategoryFilter] = useState("all");
  
  const posts = [
    {
      id: 1,
      title: "The Future of ERP Solutions in 2024",
      excerpt: "Discover how AI and cloud technology are transforming ERP systems and what it means for your business.",
      date: "March 15, 2024",
      readTime: "5 min read",
      author: "John Smith",
      category: "Technology",
      image: "photo-1488590528505-98d2b5aba04b"
    },
    {
      id: 2,
      title: "Maximizing Business Efficiency with CRM",
      excerpt: "Learn how modern CRM solutions can boost your business productivity and improve customer relationships.",
      date: "March 10, 2024",
      readTime: "4 min read",
      author: "Sarah Johnson",
      category: "Business",
      image: "photo-1486312338219-ce68d2c6f44d"
    },
    {
      id: 3,
      title: "Digital Transformation in Education",
      excerpt: "How school management systems are revolutionizing education and improving learning outcomes.",
      date: "March 5, 2024",
      readTime: "6 min read",
      author: "Michael Brown",
      category: "Education",
      image: "photo-1581091226825-a6a2a5aee158"
    },
    {
      id: 4,
      title: "Cybersecurity Best Practices",
      excerpt: "Essential security measures every business should implement to protect sensitive data.",
      date: "March 1, 2024",
      readTime: "7 min read",
      author: "Emma Wilson",
      category: "Security",
      image: "photo-1526374965328-7f61d4dc18c5"
    },
    {
      id: 5,
      title: "The Impact of 5G on Business Operations",
      excerpt: "How 5G technology is revolutionizing business operations and creating new opportunities for innovation.",
      date: "February 25, 2024",
      readTime: "8 min read",
      author: "Robert Chen",
      category: "Technology",
      image: "photo-1451187580459-43490279c0fa"
    },
    {
      id: 6,
      title: "Cloud Migration Strategies for Enterprise",
      excerpt: "A comprehensive guide to planning and executing a successful cloud migration for large organizations.",
      date: "February 20, 2024",
      readTime: "10 min read",
      author: "Jennifer Adams",
      category: "Cloud",
      image: "photo-1504639725590-34d0984388bd"
    },
    {
      id: 7,
      title: "Blockchain Applications in Supply Chain Management",
      excerpt: "Exploring how blockchain technology is transforming supply chain transparency and efficiency.",
      date: "February 15, 2024",
      readTime: "6 min read",
      author: "David Kumar",
      category: "Blockchain",
      image: "photo-1639762681057-408e52192e55"
    },
    {
      id: 8,
      title: "Artificial Intelligence in Healthcare: Opportunities and Challenges",
      excerpt: "An in-depth look at how AI is revolutionizing diagnostics, treatment, and patient care.",
      date: "February 10, 2024",
      readTime: "9 min read",
      author: "Lisa Thompson",
      category: "Healthcare",
      image: "photo-1576091160550-2173dba999ef"
    }
  ];

  const categories = ["all", ...new Set(posts.map(post => post.category.toLowerCase()))];

  const filteredPosts = useMemo(() => {
    return posts
      .filter(post => 
        (categoryFilter === "all" || post.category.toLowerCase() === categoryFilter) &&
        (post.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.author.toLowerCase().includes(searchTerm.toLowerCase()))
      )
      .sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        switch (sortOption) {
          case "newest":
            return dateB.getTime() - dateA.getTime();
          case "oldest":
            return dateA.getTime() - dateB.getTime();
          case "readTime":
            return parseInt(a.readTime) - parseInt(b.readTime);
          default:
            return 0;
        }
      });
  }, [posts, searchTerm, sortOption, categoryFilter]);

  return (
    <div className="min-h-screen py-12 bg-background">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-center mb-6">Latest Insights</h1>
        <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto mb-8">
          Stay updated with the latest insights, trends, and news in business solutions and technology.
        </p>

        <div className="mb-8 space-y-4">
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <div className="relative flex-1">
              <Input
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="flex gap-2">
              <Select value={sortOption} onValueChange={setSortOption}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                  <SelectItem value="readTime">Read Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Tabs defaultValue="all" value={categoryFilter} onValueChange={setCategoryFilter}>
            <TabsList className="mb-2 flex flex-wrap">
              {categories.map((category) => (
                <TabsTrigger key={category} value={category} className="capitalize">
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {filteredPosts.map((post) => (
            <Link 
              to={`/blog/${post.id}`} 
              key={post.id} 
              className="block transform hover:scale-105 transition-all duration-300"
            >
              <Card className="h-full hover:shadow-lg transition-shadow curved-xl">
                <div className="aspect-video overflow-hidden rounded-t-xl">
                  <img
                    src={`https://images.unsplash.com/${post.image}?auto=format&fit=crop&w=800&q=80`}
                    alt={post.title}
                    className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <Badge variant="secondary" className="rounded-full">{post.category}</Badge>
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Clock className="h-4 w-4 mr-1" />
                      {post.readTime}
                    </div>
                  </div>
                  <CardTitle className="text-xl mb-2 hover:text-orange-600 transition-colors">
                    {post.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">{post.excerpt}</p>
                  <div className="flex justify-between items-center text-sm text-muted-foreground">
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-1" />
                      {post.author}
                    </div>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {post.date}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Blog;
